import { isSoundEnabled } from './soundPreference';

// Короткие звуковые сигналы синтезируются через Web Audio API (осциллятор + огибающая громкости):
// без звуковых файлов и внешних ресурсов (не спорит с CSP), нулевой вес в бандле, работает офлайн.
// Звук — вспомогательный сигнал: при любой ошибке или недоступности API молчим, UI не роняем.

type WindowWithWebkit = Window & { webkitAudioContext?: typeof AudioContext };

let ctx: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (typeof window === 'undefined') {
    return null;
  }
  if (ctx === null) {
    const Ctor = window.AudioContext ?? (window as WindowWithWebkit).webkitAudioContext;
    if (!Ctor) {
      return null; // среда без Web Audio (напр. jsdom в тестах) — беззвучно
    }
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  return ctx;
}

/**
 * Политика автовоспроизведения браузера: AudioContext стартует в состоянии 'suspended', пока не было
 * пользовательского жеста. Разблокируем контекст на первом клике/нажатии клавиши — одноразово, оба
 * слушателя снимаются, как только сработал любой из них. Возвращает функцию отписки (для очистки
 * эффекта React, если жест ещё не случился к моменту размонтирования).
 */
export function unlockAudioOnFirstGesture(): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const remove = (): void => {
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  const unlock = (): void => {
    const context = audioContext();
    if (context && context.state === 'suspended') {
      void context.resume();
    }
    remove();
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
  return remove;
}

interface ToneStep {
  /** Частота, Гц. */
  readonly freq: number;
  /** Смещение начала от старта проигрывания, с. */
  readonly start: number;
  /** Длительность, с. */
  readonly duration: number;
  /** Пиковая громкость, 0..1. */
  readonly peak: number;
}

function playTones(steps: readonly ToneStep[]): void {
  const context = audioContext();
  if (!context) {
    return;
  }
  if (context.state === 'suspended') {
    void context.resume(); // жест уже был, но контекст успел «уснуть» — будим
  }
  const now = context.currentTime;
  for (const step of steps) {
    const osc = context.createOscillator();
    const gain = context.createGain();
    osc.type = 'sine';
    osc.frequency.value = step.freq;
    const t0 = now + step.start;
    const t1 = t0 + step.duration;
    // Экспоненциальная огибающая (нельзя стартовать с 0): быстрый подъём и мягкий спад — без щелчков.
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(step.peak, t0 + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, t1);
    osc.connect(gain).connect(context.destination);
    osc.start(t0);
    osc.stop(t1 + 0.02);
  }
}

// Ограничение частоты по виду сигнала: пачка событий (напр. несколько сообщений подряд) → один звук.
const MIN_INTERVAL_MS = 1500;
const lastPlayedAt = new Map<string, number>();

function allowed(kind: string): boolean {
  if (!isSoundEnabled()) {
    return false;
  }
  const now = Date.now();
  if (now - (lastPlayedAt.get(kind) ?? 0) < MIN_INTERVAL_MS) {
    return false;
  }
  lastPlayedAt.set(kind, now);
  return true;
}

/** Подписан документ (анкета/агентский договор/аттестация): тёплое разрешение G4 → C5 — солиднее и спокойнее двузвука чата. */
export function playSignedSound(): void {
  if (!allowed('signed')) {
    return;
  }
  playTones([
    { freq: 392.0, start: 0, duration: 0.16, peak: 0.13 }, // G4
    { freq: 523.25, start: 0.11, duration: 0.34, peak: 0.14 }, // C5
  ]);
}

/** Новое входящее сообщение в чате: восходящий двузвук E5 → B5. */
export function playChatSound(): void {
  if (!allowed('chat')) {
    return;
  }
  playTones([
    { freq: 659.25, start: 0, duration: 0.16, peak: 0.14 }, // E5
    { freq: 987.77, start: 0.12, duration: 0.22, peak: 0.14 }, // B5
  ]);
}

/** Новое уведомление в колокольчике: спокойный нисходящий двузвук A5 → E5 (контраст восходящему чату). */
export function playBellSound(): void {
  if (!allowed('bell')) {
    return;
  }
  playTones([
    { freq: 880.0, start: 0, duration: 0.14, peak: 0.12 }, // A5
    { freq: 659.25, start: 0.11, duration: 0.22, peak: 0.12 }, // E5
  ]);
}

/** Только для тестов: сбросить ограничение частоты между проверками. */
export function resetSoundThrottleForTests(): void {
  lastPlayedAt.clear();
}
